<template>
  <div v-if="localField.isUsedInFieldValues" class="tw-mb-5">
    <WdsAlert :title="$t('Caution')" warning>
      <template #description>
        {{ cautionText }}
      </template>
    </WdsAlert>
  </div>

  <div class="tw-mb-3">
    <FieldNameInput v-model="localName" :disabled="loading" :error="nameErrors" />
  </div>

  <div class="tw-mb-3">
    <SlugInput
      v-model="localSlug"
      :disable="loading"
      :error="slugErrors"
      :is-updated="localField.id !== null"
    />
  </div>

  <div v-if="fieldTypeComponent">
    <component
      :is="fieldTypeComponent"
      v-model:field="localField"
      :loading="loading"
      :validation-errors="validationErrors"
    />
  </div>

  <div class="tw-mb-3">
    <WdsRadioGroup
      name="field_type"
      :disable="loading || localField.isUsedInFieldValues"
      :label="$t('Input type')"
      :checked="localField.fieldType"
      @update:checked="fieldTypeChanged"
    >
      <WdsRadio :value="CustomFieldType.FreeText">
        <WdsText
          :sub-text="$t('Choose this if you need to be able have any text entered as a value')"
          :text="$t('Text')"
        />
      </WdsRadio>
      <WdsRadio :value="CustomFieldType.SingleSelect">
        <WdsText
          :sub-text="$t('Choose this if you need to have a defined list selected as a value')"
          :text="$t('Dropdown')"
        />
      </WdsRadio>
    </WdsRadioGroup>
  </div>

  <WdsInputArea
    v-model="localDescription"
    data-test="custom-field-description"
    :disable="loading"
    :label="$t('Description')"
    :error="descriptionErrors"
    height="70px"
    class="tw-mb-3"
  />

  <hr />

  <FeatureFlagged name="approval">
    <div v-if="appliesTo === TypeSupportingCustomFieldValues.Job">
      <div v-if="localField.approval && !localField.settings.validation.required" class="tw-mb-5">
        <WdsAlert :title="$t('Caution')" warning>
          <template #description>
            {{ $t('It is recommended to make this field required as it is enabled for approval.') }}
          </template>
        </WdsAlert>
      </div>

      <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
      <WdsToggle
        v-model:checked="localField.approval"
        :disable="loading"
        name="approval"
        class="tw-mb-3"
        :value="localField.approval"
      >
        <div class="tw-flex tw-items-center">
          <span
            data-test="enable-approval-workflows"
            class="tw-mr-1"
            v-text="$t('Enable this field for approval workflows')"
          />
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <WdsTooltip>
            <template #tooltip-content>
              {{
                $t(
                  'If you enable this field, then it will be able to be used in approval workflows.',
                )
              }}
            </template>
            <slot><WdsIcon name="info" :size="16" /></slot>
          </WdsTooltip>
        </div>
      </WdsToggle>
    </div>
  </FeatureFlagged>

  <WdsToggle
    v-model:checked="localField.settings.validation.required"
    :disable="loading"
    :error="settingsErrors"
    class="tw-mb-3"
  >
    <span data-test="custom-field-required" v-text="$t('Make this field required')" />
  </WdsToggle>

  <WdsToggle
    v-model:checked="localField.apiOnly"
    data-test="custom-field-api-only"
    :disable="loading"
    :value="localField.apiOnly"
  >
    {{ $t('Make this field updateable through the api only') }}
  </WdsToggle>
</template>

<script setup lang="ts">
import { computed, toRef, Ref } from 'vue'
import {
  WdsAlert,
  WdsRadioGroup,
  WdsIcon,
  WdsInputArea,
  WdsTooltip,
  WdsToggle,
  WdsRadio,
  WdsText,
} from '@worksome/wds-components'
import { useVModel } from '@vueuse/core'
import { $t } from '@/composables/translation'
import { CustomFieldType, TypeSupportingCustomFieldValues } from '@/gql/graphql'
import FieldSingleSelect from '@/components/Fields/fieldTypeComponents/FieldSingleSelect.vue'
import { CustomField } from '@/components/Fields/dataObjects/CustomField'
import {
  getCustomFieldCautionText,
  newInstanceOfCustomField,
} from '@/components/Fields/utils/CustomFieldUtils'
import { useGraphQlError } from '@/composables/errors'
import FeatureFlagged from '@/components/FeatureFlagged.vue'
import SlugInput from '@/components/Fields/commonFields/SlugInput.vue'
import FieldNameInput from '@/components/Fields/commonFields/FieldNameInput.vue'

const emits = defineEmits(['update:field', 'fieldTypeChanged'])

export interface Props {
  field: CustomField
  validationErrors?: Object
  loading: boolean
  appliesTo: TypeSupportingCustomFieldValues
}

const props = withDefaults(defineProps<Props>(), {
  validationErrors: () => ({}),
})

const mapFieldTypeComponent = {
  [CustomFieldType.SingleSelect]: FieldSingleSelect,
}

const localField: Ref<CustomField> = useVModel(props, 'field', emits)

const localName = computed<string | undefined>({
  get: () => localField.value.title ?? undefined,
  set: (newValue) => {
    localField.value.title = newValue ?? null
  },
})

const localSlug = computed<string | undefined>({
  get: () => localField.value.slug ?? undefined,
  set: (newValue) => {
    localField.value.slug = newValue ?? null
  },
})

const localDescription = computed<string | undefined>({
  get: () => localField.value.description ?? undefined,
  set: (newValue) => {
    localField.value.description = newValue ?? null
  },
})

const fieldTypeComponent = computed(() => mapFieldTypeComponent[localField.value.fieldType])

const errors = toRef(props, 'validationErrors')

const nameErrors = useGraphQlError(errors, 'title')
const descriptionErrors = useGraphQlError(errors, 'description')
const settingsErrors = useGraphQlError(errors, 'settings')
const slugErrors = useGraphQlError(errors, 'slug')

function fieldTypeChanged(newType: CustomFieldType) {
  if (newType !== localField.value.fieldType) {
    const clone = { ...localField.value }
    localField.value = newInstanceOfCustomField(
      {
        id: clone.id!,
        appliesTo: clone.appliesTo,
        visibility: clone.visibility,
        title: clone.title!,
        description: clone.description!,
        approval: clone.approval!,
        isUsedInFieldValues: clone.isUsedInFieldValues,
        apiOnly: clone.apiOnly,
      },
      newType,
    )

    emits('fieldTypeChanged', newType)
  }
}

const cautionText = computed(() => getCustomFieldCautionText(props.appliesTo))
</script>
